import { useMutation, useQueryClient } from "@tanstack/react-query";

import useApiHelper from "../../hooks/useApiHelper";
import { IPartCategory, IPartProTradeSyncSummary } from "../../models/Category/Category";
import { IDefaultResponse } from "../../models/DefaultResponse";
import useDefaultCRUDHandlers from "../../hooks/useDefaultCRUDHandlers";
import useNotification from "../../components/shared/Notifications/useNotifications";
import { CategoryQueryKeys } from "./keys";

export default function useCategoryMutations() {
  const { post, put, del } = useApiHelper();
  const queryClient = useQueryClient();
  const { saveHandlers } = useDefaultCRUDHandlers("Category sync");
  const { addNotification } = useNotification();

  const create = useMutation({
    mutationFn: (category: IPartCategory) => post(`/part/categories`, category),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CategoryQueryKeys.all() });
    },
  });

  const update = useMutation({
    mutationFn: (category: IPartCategory) => put(`/part/categories/${category.id}`, category),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CategoryQueryKeys.all() });
    },
  });

  const remove = useMutation({
    mutationFn: (categoryId: string) => del(`/part/categories/${categoryId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CategoryQueryKeys.all() });
    },
  });

  const syncCategories = useMutation({
    mutationFn: (customerIds: string[]) =>
      post(`/part/categories/sync`, { customers: customerIds }),
    onSuccess: () => {
      saveHandlers.onSuccess();
    },
    onError: () => {
      saveHandlers.onError();
    },
  });

  // Pushes the full category list to PartPro trade now instead of waiting for the nightly run.
  const pushPartProTrade = useMutation({
    mutationFn: () =>
      post<IDefaultResponse<IPartProTradeSyncSummary>>(`/part/categories/push-partpro-trade`, {}),
    onSuccess: (res) => {
      const s = res.data;
      addNotification({
        variant: "success",
        primaryText: "PartPro trade categories synced",
        secondaryText: s
          ? `${s.created} created, ${s.updated} updated, ${s.adopted} adopted, ${s.hidden} hidden, ${s.unchanged} unchanged`
          : res.message,
      });
    },
    onError: (error: Error) => {
      addNotification({
        variant: "error",
        primaryText: "Failed to sync PartPro trade categories",
        secondaryText: error.message,
      });
    },
  });

  return {
    create,
    update,
    remove,
    syncCategories,
    pushPartProTrade,
  };
}
